import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listGizmosGames, createGizmosGame } from '../api/gizmos';
import { useGizmosLobbyWebSocket } from '../hooks/useWebSocket';

export default function GizmosLobbyPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [error, setError] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(2);
  const [creating, setCreating] = useState(false);

  const fetchGames = useCallback(async () => {
    try {
      const { data } = await listGizmosGames();
      setGames(data);
    } catch {
      setError('Failed to load games');
    }
  }, []);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  useGizmosLobbyWebSocket(
    useCallback((msg) => {
      if (msg.type === 'lobby.update') fetchGames();
    }, [fetchGames]),
  );

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const { data } = await createGizmosGame({ max_players: maxPlayers });
      navigate(`/gizmos/${data.id}/room`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create game');
    } finally {
      setCreating(false);
    }
  };

  const statusLabel = { waiting: '等待中', ready: '准备就绪', in_progress: '进行中', finished: '已结束' };

  return (
    <div className="lobby-page">
      <div className="lobby-header">
        <h1>Gizmos 大厅</h1>
        <div className="lobby-nav">
          <Link to="/home" className="back-link">← 游戏中心</Link>
          <button onClick={logout} className="btn-secondary">登出 ({user?.username})</button>
        </div>
      </div>

      <div className="create-game-section">
        <h2>创建新游戏</h2>
        <label>
          玩家人数：
          <select value={maxPlayers} onChange={e => setMaxPlayers(Number(e.target.value))}>
            <option value={2}>2人</option>
            <option value={3}>3人</option>
            <option value={4}>4人</option>
          </select>
        </label>
        <button onClick={handleCreate} disabled={creating} className="btn-primary">
          {creating ? '创建中...' : '创建游戏'}
        </button>
        {error && <p className="error-text">{error}</p>}
      </div>

      <div className="games-list">
        <h2>游戏列表</h2>
        {games.length === 0 && <p className="empty-hint">暂无游戏，创建一个吧！</p>}
        {games.map(game => (
          <div key={game.id} className="game-card">
            <div className="game-info">
              <span className="game-id">#{game.id}</span>
              <span className={`game-status status-${game.status}`}>{statusLabel[game.status] || game.status}</span>
              <span>{game.players.length}/{game.max_players} 玩家</span>
              <span>{game.players.map(p => p.username).join(', ')}</span>
            </div>
            <div className="game-actions">
              {(game.status === 'waiting' || game.status === 'ready') && (
                <button className="btn-primary" onClick={() => navigate(`/gizmos/${game.id}/room`)}>
                  加入/查看
                </button>
              )}
              {game.status === 'in_progress' && (
                <button className="btn-primary" onClick={() => navigate(`/gizmos/${game.id}/play`)}>
                  继续游戏
                </button>
              )}
              {game.status === 'finished' && (
                <button className="btn-secondary" onClick={() => navigate(`/gizmos/${game.id}/play`)}>
                  查看结果
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
